import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MessageSquare, Phone, Mail, Clock, Loader2 } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import apiService from '@/services/api';

interface ContactInfo {
  phone?: string;
  email?: string;
}

const SupportPage = () => {
  const [contact, setContact] = useState<ContactInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await apiService.get('/public/contact-info');
        if (response?.data) {
          setContact(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch contact info:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContactInfo();
  }, []);

  return (
    <>
      <Helmet>
        <title>Support | Virewire Property Services</title>
        <meta name="description" content="Get support for your Virewire property services account." />
      </Helmet>

      <DashboardLayout>
        <div className="w-full">
          <h1 className="text-4xl font-bold text-[#00543b] mb-8">Support & Help</h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Contact Methods */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="h-5 w-5 text-[#00543b]" />
                  Contact Us
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-[#00543b]" />
                ) : (
                  <>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Phone Support</p>
                      {contact?.phone ? (
                        <a href={`tel:${contact.phone}`} className="font-semibold text-[#00543b] hover:underline">
                          {contact.phone}
                        </a>
                      ) : (
                        <p className="font-semibold text-gray-500">Not available</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Email Support</p>
                      {contact?.email ? (
                        <a href={`mailto:${contact.email}`} className="font-semibold text-[#00543b] hover:underline">
                          {contact.email}
                        </a>
                      ) : (
                        <p className="font-semibold text-gray-500">Not available</p>
                      )}
                    </div>
                  </>
                )}
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span>Available 24/7</span>
                </div>
              </CardContent>
            </Card>

            {/* Email Us */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-[#00543b]" />
                  Email Us
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">Send our support team a message and we'll get back to you</p>
                <Button
                  className="w-full bg-[#00543b] hover:bg-[#003d2d]"
                  disabled={!contact?.email}
                  onClick={() => contact?.email && (window.location.href = `mailto:${contact.email}`)}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Email Support
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* FAQ Section */}
          <Card>
            <CardHeader>
              <CardTitle>Frequently Asked Questions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">How do I request a service?</h3>
                  <p className="text-sm text-gray-600">Click on "Request a Service" from the menu to get started with a new service request.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">How long does it take to get a quote?</h3>
                  <p className="text-sm text-gray-600">Most quotes are provided within 24-48 hours. You'll receive notifications as our providers review your request.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Can I cancel or reschedule a booking?</h3>
                  <p className="text-sm text-gray-600">Yes, you can manage your bookings from the "Active Orders" section. Cancellations must be made 24 hours in advance.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    </>
  );
};

export default SupportPage;
